import React, { createContext, useState, useContext, useEffect } from 'react';
import {
  signup as signupApi,
  login as loginApi,
  googleLogin as googleLoginApi,
  appleLogin as appleLoginApi,
  verifyToken,
  verifyLoginOtp as verifyLoginOtpApi,
  loginMobile as loginMobileApi,
  verifyPhone as verifyPhoneApi,
  logoutSession as logoutSessionApi,
} from '../services/authApi';

const ACCESS_KEY = 'yovo_auth_token';
const REFRESH_KEY = 'yovo_refresh_token';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function persistTokens(data) {
  const access = data.accessToken || data.token;
  if (access) {
    localStorage.setItem(ACCESS_KEY, access);
  }
  if (data.refreshToken) {
    localStorage.setItem(REFRESH_KEY, data.refreshToken);
  } else {
    // Web logins intentionally omit refresh — clear any stale app refresh
    localStorage.removeItem(REFRESH_KEY);
  }
  return access;
}

function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiresPhone, setRequiresPhone] = useState(false);

  // Check for existing token on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem(ACCESS_KEY);
        if (storedToken) {
          const data = await verifyToken(storedToken);
          if (data.success && data.user) {
            setToken(storedToken);
            setUser(data.user);
            if (data.requiresPhone) setRequiresPhone(true);
          } else {
            clearTokens();
          }
        }
      } catch (error) {
        // Token invalid or expired — leave refresh for api interceptor / next login
        clearTokens();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const applyAuthSuccess = (data) => {
    const access = persistTokens(data);
    setToken(access);
    setUser(data.user);
    if (data.requiresPhone) setRequiresPhone(true);
    else if (data.user?.phone_number) setRequiresPhone(false);
  };

  const signup = async (userData) => {
    try {
      const data = await signupApi(userData);
      if (data.success && (data.token || data.accessToken) && data.user) {
        applyAuthSuccess(data);
        return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0 };
      }
      throw new Error('Signup failed');
    } catch (error) {
      throw error;
    }
  };

  const login = async (username, password) => {
    try {
      const data = await loginApi(username, password);
      if (data.success && (data.token || data.accessToken) && data.user) {
        applyAuthSuccess(data);
        return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0, requiresPhone: data.requiresPhone };
      }
      throw new Error('Login failed');
    } catch (error) {
      throw error;
    }
  };

  const googleLogin = async (credential) => {
    try {
      const data = await googleLoginApi(credential);
      if (data.success && (data.token || data.accessToken) && data.user) {
        applyAuthSuccess(data);
        return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0, requiresPhone: data.requiresPhone };
      }
      throw new Error('Google login failed');
    } catch (error) {
      throw error;
    }
  };

  const appleLogin = async (identityToken, fullName) => {
    try {
      const data = await appleLoginApi(identityToken, fullName);
      if (data.success && (data.token || data.accessToken) && data.user) {
        applyAuthSuccess(data);
        return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0, requiresPhone: data.requiresPhone };
      }
      throw new Error('Apple login failed');
    } catch (error) {
      throw error;
    }
  };

  const mobileOtpLogin = async (phone, otp) => {
    const data = await verifyLoginOtpApi(phone, otp);
    if (data.success && data.needsSignup) {
      return { success: true, needsSignup: true, verifiedPhone: data.verifiedPhone };
    }
    if (data.success && (data.token || data.accessToken) && data.user) {
      applyAuthSuccess(data);
      return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0 };
    }
    throw new Error('OTP login failed');
  };

  const mobilePasswordLogin = async (phone, password) => {
    const data = await loginMobileApi(phone, password);
    if (data.success && (data.token || data.accessToken) && data.user) {
      applyAuthSuccess(data);
      return { success: true, user: data.user, claimedBookings: data.claimedBookings || 0 };
    }
    throw new Error('Mobile login failed');
  };

  const linkPhone = async (phone, otp) => {
    const storedToken = token || localStorage.getItem(ACCESS_KEY);
    const data = await verifyPhoneApi(phone, otp, storedToken);
    if (data.success) {
      setUser(prev => ({ ...prev, phone_number: data.phone_number }));
      setRequiresPhone(false);
      localStorage.removeItem('phone_prompt_dismissed');
    }
    return data;
  };

  const logout = () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    // Best-effort server revoke (fire-and-forget)
    logoutSessionApi({ refreshToken: refreshToken || undefined }).catch(() => {});
    clearTokens();
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    loading,
    requiresPhone,
    setRequiresPhone,
    signup,
    login,
    googleLogin,
    appleLogin,
    mobileOtpLogin,
    mobilePasswordLogin,
    linkPhone,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
